/**
 * Column Customizer Component
 *
 * Allows users to show/hide and reorder table columns.
 * Persists preferences in localStorage.
 */

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Settings2, GripVertical, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  locked?: boolean; // Cannot be hidden
  order: number;
}

export interface ColumnCustomizerProps {
  /** Unique key for localStorage persistence */
  storageKey: string;
  /** Column definitions */
  columns: Omit<ColumnConfig, "visible" | "order">[];
  /** Default visible columns (by id) */
  defaultVisible?: string[];
  /** Callback when column config changes */
  onChange: (columns: ColumnConfig[]) => void;
  /** Current column config (controlled mode) */
  value?: ColumnConfig[];
  /** Class name for the trigger button */
  className?: string;
}

function getStoredConfig(
  storageKey: string,
  defaultColumns: Omit<ColumnConfig, "visible" | "order">[],
  defaultVisible?: string[]
): ColumnConfig[] {
  try {
    const stored = localStorage.getItem(`column-config-${storageKey}`);
    if (stored) {
      const parsed = JSON.parse(stored) as ColumnConfig[];
      // Merge with defaults in case new columns were added
      const storedIds = new Set(parsed.map(c => c.id));
      const mergedColumns = [...parsed];
      defaultColumns.forEach((col, index) => {
        if (!storedIds.has(col.id)) {
          mergedColumns.push({
            ...col,
            visible: defaultVisible ? defaultVisible.includes(col.id) : true,
            order: mergedColumns.length,
          });
        }
      });
      return mergedColumns;
    }
  } catch (e) {
    console.error("Failed to parse stored column config:", e);
  }

  // Return defaults
  return defaultColumns.map((col, index) => ({
    ...col,
    visible: defaultVisible ? defaultVisible.includes(col.id) : true,
    order: index,
  }));
}

function saveConfig(storageKey: string, config: ColumnConfig[]) {
  try {
    localStorage.setItem(`column-config-${storageKey}`, JSON.stringify(config));
  } catch (e) {
    console.error("Failed to save column config:", e);
  }
}

export function ColumnCustomizer({
  storageKey,
  columns: defaultColumns,
  defaultVisible,
  onChange,
  value,
  className,
}: ColumnCustomizerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localConfig, setLocalConfig] = useState<ColumnConfig[]>(() =>
    getStoredConfig(storageKey, defaultColumns, defaultVisible)
  );
  const [draggedId, setDraggedId] = useState<string | null>(null);

  // Use controlled value if provided
  const config = value ?? localConfig;
  const setConfig = useCallback(
    (
      newConfig: ColumnConfig[] | ((prev: ColumnConfig[]) => ColumnConfig[])
    ) => {
      const updated =
        typeof newConfig === "function" ? newConfig(config) : newConfig;
      setLocalConfig(updated);
      saveConfig(storageKey, updated);
      onChange(updated);
    },
    [config, onChange, storageKey]
  );

  // Initialize on mount
  useEffect(() => {
    onChange(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = (id: string) => {
    setConfig(prev =>
      prev.map(col =>
        col.id === id && !col.locked ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) return;

    setConfig(prev => {
      const items = [...prev];
      const draggedIndex = items.findIndex(c => c.id === draggedId);
      const targetIndex = items.findIndex(c => c.id === targetId);

      if (draggedIndex === -1 || targetIndex === -1) return prev;

      const [draggedItem] = items.splice(draggedIndex, 1);
      items.splice(targetIndex, 0, draggedItem);

      return items.map((item, index) => ({ ...item, order: index }));
    });
  };

  const handleDragEnd = () => {
    setDraggedId(null);
  };

  const handleReset = () => {
    const resetConfig = defaultColumns.map((col, index) => ({
      ...col,
      visible: defaultVisible ? defaultVisible.includes(col.id) : true,
      order: index,
    }));
    setConfig(resetConfig);
  };

  const sortedConfig = [...config].sort((a, b) => a.order - b.order);
  const visibleCount = config.filter(c => c.visible).length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn("gap-2", className)}>
          <Settings2 className="h-4 w-4" />
          <span className="hidden sm:inline">Columns</span>
          {visibleCount !== config.length && (
            <span className="text-xs text-muted-foreground">
              ({visibleCount}/{config.length})
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Customize Columns</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="h-8 px-2 text-xs"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Drag to reorder. Check to show/hide.
          </p>

          <div className="space-y-1">
            {sortedConfig.map(column => (
              <div
                key={column.id}
                draggable={!column.locked}
                onDragStart={e => handleDragStart(e, column.id)}
                onDragOver={e => handleDragOver(e, column.id)}
                onDragEnd={handleDragEnd}
                className={cn(
                  "flex items-center gap-2 p-2 rounded-md transition-colors",
                  draggedId === column.id && "opacity-50 bg-muted",
                  !column.locked && "cursor-move hover:bg-muted/50"
                )}
              >
                {!column.locked && (
                  <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )}
                {column.locked && <div className="w-4" />}
                <Checkbox
                  id={`col-${column.id}`}
                  checked={column.visible}
                  disabled={column.locked}
                  onCheckedChange={() => handleToggle(column.id)}
                />
                <Label
                  htmlFor={`col-${column.id}`}
                  className={cn(
                    "flex-1 text-sm cursor-pointer",
                    column.locked && "text-muted-foreground"
                  )}
                >
                  {column.label}
                  {column.locked && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      (required)
                    </span>
                  )}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Hook to manage column customization state
 */
export function useColumnCustomization(
  storageKey: string,
  defaultColumns: Omit<ColumnConfig, "visible" | "order">[],
  defaultVisible?: string[]
) {
  const [columns, setColumns] = useState<ColumnConfig[]>(() =>
    getStoredConfig(storageKey, defaultColumns, defaultVisible)
  );

  const visibleColumns = columns
    .filter(c => c.visible)
    .sort((a, b) => a.order - b.order);

  const isVisible = useCallback(
    (id: string) => columns.find(c => c.id === id)?.visible ?? false,
    [columns]
  );

  return {
    columns,
    setColumns,
    visibleColumns,
    isVisible,
  };
}

export default ColumnCustomizer;
